/**
 * default-destination.ts — the sign-in destination default (ADR-113 D22).
 *
 * > **The sign-in destination modal appears only when a person genuinely has
 * > more than one destination.** Each entry is badged so an operator role is
 * > visually distinct from the person's own account. **The person may set a
 * > default and stop being asked**, and may switch at any time from the account
 * > menu. A person with one destination never sees it.
 *
 * `ContextSelectModal` already implements the badging and the ">1 destination"
 * rule. This module is the part that was missing: the settable default, and the
 * decision of what to do on sign-in given one.
 *
 * ⭐⭐ KEYED PER USER, NEVER GLOBALLY — and this project has already paid for
 * getting that wrong. `active-domain-store.ts` records the 15 July 2026 incident:
 * ADR-097 persisted the active domain under ONE global key shared by every
 * account and context, so merely VIEWING another context's dashboard overwrote
 * the pointer for the operator's own account. "One key cannot hold N contexts'
 * choices." The same shape applies here with a worse blast radius — a shared
 * default would send one person to another person's chosen destination on a
 * shared device. So the key carries the `cognitoSub`, and a default is scoped to
 * the identity that set it by construction rather than by teardown ordering.
 *
 * ⚠ A STORED DEFAULT IS A PREFERENCE, NEVER AN AUTHORISATION. It decides which
 * of the destinations the SERVER already returned to land on. It can never add a
 * destination, and {@link resolveSignInDestination} refuses to honour one that is
 * not in the current context list — see below.
 */
import type { PortalContext } from '../hooks/useAuth';

/** `nexa-default-destination:<cognitoSub>` — see the header for why it is per-user. */
export const DEFAULT_DESTINATION_KEY_PREFIX = 'nexa-default-destination';

export const defaultDestinationKey = (cognitoSub: string): string =>
  `${DEFAULT_DESTINATION_KEY_PREFIX}:${cognitoSub}`;

/**
 * A stable key for one destination.
 *
 * An account is identified by its accountId; a portal by BOTH its type and its
 * orgId, because one person can operate several orgs of the same type and the
 * type alone would collapse them into one another.
 */
export function destinationKey(c: PortalContext): string {
  return c.kind === 'account' ? `a:${c.accountId}` : `p:${c.portalType}:${c.orgId}`;
}

/** Persist the person's chosen default. Silently no-ops when storage is unavailable
 *  (private browsing, quota) — a preference that cannot be saved must never break
 *  sign-in, and the person simply gets asked again. */
export function writeDefaultDestination(cognitoSub: string, key: string): void {
  try {
    localStorage.setItem(defaultDestinationKey(cognitoSub), key);
  } catch {
    /* preference only — never fatal */
  }
}

export function readDefaultDestination(cognitoSub: string | undefined): string | null {
  if (!cognitoSub) return null;
  try {
    return localStorage.getItem(defaultDestinationKey(cognitoSub));
  } catch {
    return null;
  }
}

/** Forget the default, so the chooser returns. */
export function clearDefaultDestination(cognitoSub: string | undefined): void {
  if (!cognitoSub) return;
  try {
    localStorage.removeItem(defaultDestinationKey(cognitoSub));
  } catch {
    /* no-op */
  }
}

/**
 * Purge every stored default, whoever set it — called on sign-out alongside the
 * other per-session pointers. A default belongs to the sign-in that set it; a
 * shared device must not carry one person's choice into the next person's
 * session.
 */
export function clearAllDefaultDestinations(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(DEFAULT_DESTINATION_KEY_PREFIX)) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    /* no-op */
  }
}

export type SignInDestination =
  /** Land here without asking — either the only destination, or the stored default. */
  | { action: 'go'; context: PortalContext; because: 'only_destination' | 'stored_default' }
  /** Show the chooser. */
  | { action: 'ask'; because: 'multiple_destinations' | 'stale_default' | 'no_destinations' };

/**
 * What should happen at sign-in?
 *
 * The whole of D22 in one pure function, so every branch is testable without a
 * browser:
 *
 *   · exactly one destination  → go, never ask ("A person with one destination
 *                                never sees it")
 *   · a stored default that is STILL VALID → go
 *   · anything else            → ask
 *
 * ⛔⛔ A STORED DEFAULT IS HONOURED ONLY IF IT IS STILL IN THE CURRENT LIST, and
 * this is the load-bearing check. Contexts are revocable: a consultant connection
 * ends, a partner attachment is suspended, an operator row is retired. A default
 * pointing at a destination the person no longer holds must NOT be followed —
 * following it would navigate into a portal they have lost, producing a 403 they
 * cannot explain, or worse a half-rendered surface. Falling back to the chooser
 * shows them what they DO have. Note the direction: the server's context list is
 * the authority and the stored preference is filtered THROUGH it, never merged
 * INTO it, so a tampered localStorage value cannot manufacture a destination.
 */
export function resolveSignInDestination(
  contexts: readonly PortalContext[],
  cognitoSub: string | undefined,
): SignInDestination {
  if (contexts.length === 0) return { action: 'ask', because: 'no_destinations' };

  if (contexts.length === 1) {
    return { action: 'go', context: contexts[0], because: 'only_destination' };
  }

  const stored = readDefaultDestination(cognitoSub);
  if (stored) {
    const match = contexts.find((c) => destinationKey(c) === stored);
    if (match) return { action: 'go', context: match, because: 'stored_default' };
    // The default named something they no longer hold. Ask rather than navigate.
    return { action: 'ask', because: 'stale_default' };
  }

  return { action: 'ask', because: 'multiple_destinations' };
}

/**
 * Is this context the person's current default? Drives the "Default" marker and
 * the set/unset control in the chooser.
 */
export function isDefaultDestination(
  c: PortalContext,
  cognitoSub: string | undefined,
): boolean {
  const stored = readDefaultDestination(cognitoSub);
  return stored !== null && stored === destinationKey(c);
}
